// "Trends" — 7d / 30d toggle. /api/usage now returns PER-DAY rows
// ({ day, service, resource, total }), so we render two real time-series:
//   Chart 1 "Requests / day" — a line per service (resource http_requests or
//     workers_requests, summed per day).
//   Chart 2 "5xx / day" — a bar per day of total errors (http_5xx + workers_errors).
// The x-axis is the distinct sorted days in the range. Dark-aware colours; per-service
// dash patterns distinguish lines. Self-fetching; loading / empty / error + dark.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  type ChartData,
  type ChartOptions,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { api, ApiError, type UsageRow } from "../lib/api";
import { Card, SectionTitle } from "./ui";
import { useThemeStore } from "../lib/theme";
import { serviceLabel } from "../lib/format";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

type Range = "7d" | "30d";

// Resources that count as "requests" and as "5xx / errors".
const REQUEST_RESOURCES = new Set(["http_requests", "workers_requests"]);
const ERROR_RESOURCES = new Set(["http_5xx", "workers_errors"]);

/** Compact large counts → 1.2M / 3.4K / 950. Local helper (avoid importing big()). */
function big(n: number | null | undefined): string {
  const v = typeof n === "number" && Number.isFinite(n) ? n : 0;
  if (Math.abs(v) >= 1e9) return (v / 1e9).toFixed(v >= 1e10 ? 0 : 1) + "B";
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(v >= 1e7 ? 0 : 1) + "M";
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(0) + "K";
  return String(Math.round(v));
}

/** Y-axis tick: one decimal under 10K so adjacent ticks never collapse ("1.5K" not "2K"). */
function yTick(n: number): string {
  const v = Number.isFinite(n) ? n : 0;
  const a = Math.abs(v);
  if (a >= 1e6) return (v / 1e6).toFixed(a < 1e7 ? 1 : 0) + "M";
  if (a >= 1e3) return (v / 1e3).toFixed(a < 1e4 ? 1 : 0) + "K";
  return String(v);
}

/** Full UTC day range (YYYY-MM-DD) of `n` days ending today — for zero-filled axes. */
function fullDayRange(n: number): string[] {
  const now = new Date();
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i),
    );
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

/** "2026-06-07" → "Jun 7" for axis labels. */
function dayLabel(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return day;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

// A small dark-aware palette keyed by service index.
const PALETTE = [
  "#3b82f6", // blue-500
  "#f59e0b", // amber-500
  "#22c55e", // green-500
  "#a855f7", // purple-500
  "#ec4899", // pink-500
  "#14b8a6", // teal-500
  "#ef4444", // red-500
];

// Dash patterns to distinguish services when colours alone aren't enough.
const DASHES: number[][] = [[], [6, 4], [2, 3], [8, 3, 2, 3], [10, 4]];

function ToggleBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "rounded-md border px-3 py-1 text-xs font-medium transition-colors " +
        (active
          ? "border-blue-600 bg-blue-600 text-white"
          : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700")
      }
    >
      {children}
    </button>
  );
}

interface Pivoted {
  days: string[];
  services: string[];
  /** service → day → request total */
  requestsByService: Map<string, Map<string, number>>;
  /** day → 5xx/error total (summed across services) */
  errorsByDay: Map<string, number>;
}

/** Pivot flat per-day rows into the series the two charts need.
 *  The x-axis is the FULL `rangeDays` window ending today (UTC) — missing days
 *  zero-fill so a sparse range doesn't collapse the axis. */
function pivot(rows: UsageRow[], rangeDays: number): Pivoted {
  const services = new Set<string>();
  const requestsByService = new Map<string, Map<string, number>>();
  const errorsByDay = new Map<string, number>();

  for (const r of rows) {
    const total = Number(r.total) || 0;

    if (REQUEST_RESOURCES.has(r.resource)) {
      services.add(r.service);
      let byDay = requestsByService.get(r.service);
      if (!byDay) {
        byDay = new Map<string, number>();
        requestsByService.set(r.service, byDay);
      }
      byDay.set(r.day, (byDay.get(r.day) ?? 0) + total);
    }

    if (ERROR_RESOURCES.has(r.resource)) {
      errorsByDay.set(r.day, (errorsByDay.get(r.day) ?? 0) + total);
    }
  }

  return {
    days: fullDayRange(rangeDays),
    services: [...services].sort(),
    requestsByService,
    errorsByDay,
  };
}

export default function TrendsSection({ refreshTick }: { refreshTick?: number }) {
  const [range, setRange] = useState<Range>("7d");
  const [rows, setRows] = useState<UsageRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const theme = useThemeStore((s) => s.theme);
  const dark = theme === "dark";

  // Reset to the loading state only on a range switch — auto-refresh ticks keep
  // the current chart on screen while fresh data loads (no flicker every 60s).
  const prevRange = useRef(range);

  useEffect(() => {
    let alive = true;
    if (prevRange.current !== range) {
      prevRange.current = range;
      setRows(null);
    }
    setError(null);
    api
      .usage(range)
      .then((r) => {
        if (alive) setRows(r);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : "Failed to load usage");
      });
    return () => {
      alive = false;
    };
  }, [range, refreshTick]);

  const piv = useMemo(() => pivot(rows ?? [], range === "7d" ? 7 : 30), [rows, range]);

  const tickColor = dark ? "#cbd5e1" : "#475569";
  const gridColor = dark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.15)";
  const legendColor = dark ? "#e2e8f0" : "#334155";

  const labels = useMemo(() => piv.days.map(dayLabel), [piv.days]);

  // Chart 1 — Requests / day, one line per service.
  const requestsData: ChartData<"line"> = useMemo(
    () => ({
      labels,
      datasets: piv.services.map((svc, i) => {
        const byDay = piv.requestsByService.get(svc);
        const color = PALETTE[i % PALETTE.length];
        return {
          label: serviceLabel(svc),
          data: piv.days.map((d) => byDay?.get(d) ?? 0),
          borderColor: color,
          backgroundColor: color,
          borderDash: DASHES[i % DASHES.length],
          pointRadius: 2,
          pointHoverRadius: 4,
          borderWidth: 2,
          tension: 0.25,
        };
      }),
    }),
    [labels, piv.services, piv.requestsByService, piv.days],
  );

  const requestsOptions: ChartOptions<"line"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "index", intersect: false },
      scales: {
        x: { grid: { display: false }, ticks: { color: tickColor } },
        y: {
          beginAtZero: true,
          grid: { color: gridColor },
          ticks: { color: tickColor, maxTicksLimit: 6, callback: (v) => yTick(Number(v)) },
        },
      },
      plugins: {
        legend: { display: true, labels: { color: legendColor, boxWidth: 12 } },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${big(Number(ctx.parsed.y))}`,
          },
        },
      },
    }),
    [tickColor, gridColor, legendColor],
  );

  // Chart 2 — 5xx / day (total across services), single bar series.
  const errorsData: ChartData<"bar"> = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: "5xx / errors",
          data: piv.days.map((d) => piv.errorsByDay.get(d) ?? 0),
          backgroundColor: "#ef4444", // red-500
          borderRadius: 4,
        },
      ],
    }),
    [labels, piv.days, piv.errorsByDay],
  );

  const errorsOptions: ChartOptions<"bar"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      scales: {
        x: { grid: { display: false }, ticks: { color: tickColor } },
        y: {
          beginAtZero: true,
          grid: { color: gridColor },
          ticks: { color: tickColor, maxTicksLimit: 6, callback: (v) => yTick(Number(v)) },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.label}: ${big(Number(ctx.parsed.y))} 5xx`,
          },
        },
      },
    }),
    [tickColor, gridColor],
  );

  const rangeLabel = range === "7d" ? "7 days" : "30 days";
  // Days are now zero-filled, so "any data" comes from the raw rows instead.
  const hasDays = (rows ?? []).length > 0;
  const hasRequests = piv.services.length > 0;
  const hasErrors = piv.days.some((d) => (piv.errorsByDay.get(d) ?? 0) > 0);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <SectionTitle>Trends</SectionTitle>
        <div className="flex gap-2">
          <ToggleBtn active={range === "7d"} onClick={() => setRange("7d")}>
            7 days
          </ToggleBtn>
          <ToggleBtn active={range === "30d"} onClick={() => setRange("30d")}>
            30 days
          </ToggleBtn>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Chart 1 — Requests / day */}
        <Card>
          <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
            Requests / day — last {rangeLabel}
          </div>
          <div className="h-64 w-full">
            {error ? (
              <div className="flex h-full items-center justify-center text-sm text-red-600 dark:text-red-400">
                ⚠️ {error}
              </div>
            ) : rows === null ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-400 dark:text-gray-500">
                Loading…
              </div>
            ) : !hasRequests ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                No request data for this range.
              </div>
            ) : (
              <Line data={requestsData} options={requestsOptions} />
            )}
          </div>
        </Card>

        {/* Chart 2 — 5xx / day */}
        <Card>
          <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
            5xx / day — last {rangeLabel}
          </div>
          <div className="h-64 w-full">
            {error ? (
              <div className="flex h-full items-center justify-center text-sm text-red-600 dark:text-red-400">
                ⚠️ {error}
              </div>
            ) : rows === null ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-400 dark:text-gray-500">
                Loading…
              </div>
            ) : !hasDays ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                No usage data for this range.
              </div>
            ) : !hasErrors ? (
              <div className="flex h-full items-center justify-center text-sm text-green-600 dark:text-green-400">
                ✅ No 5xx in this range.
              </div>
            ) : (
              <Bar data={errorsData} options={errorsOptions} />
            )}
          </div>
        </Card>
      </div>
    </section>
  );
}
